/* @ngInject */

export default ($rootScope, Liability, LiabilityType, helperService) => {
  /**
   * Get all liability types.
   *
   * @returns {Array}
   */
  const getAllLiabilityTypes = () => Liability.getAvailableLiabilityTypes();

  /**
   * Get available liability types.
   *
   * @returns {Array}
   */
  const getAvailableLiabilityTypes = () => {
    if (helperService.getOrganizationCountry().cca2 === 'NZ') {
      let liabilityTypes = Liability.getAvailableLiabilityTypes();
      if (liabilityTypes && liabilityTypes.length) {
        const idx = liabilityTypes.findIndex(
          (l) => l.id === Liability.getAvailableLiabilityTypes()[5].id
        );
        if (idx > -1) {
          liabilityTypes = _.reject(liabilityTypes, (_, index) => index === idx);
        }
      }
      return liabilityTypes;
    }
    if (helperService.getOrganizationCountry().cca2 === 'AU') {
      let liabilityTypes = Liability.getAvailableLiabilityTypes();
      if (liabilityTypes && liabilityTypes.length) {
        const idx = liabilityTypes.findIndex(
          (l) => l.id === Liability.getAvailableLiabilityTypes()[2].id
        );
        if (idx > -1) {
          liabilityTypes = _.reject(liabilityTypes, (_, index) => index === idx);
        }
      }
      return liabilityTypes;
    }
    return Liability.getAvailableLiabilityTypes();
  };

  /**
   * Get liability types.
   *
   * @returns {Array}
   */
  const getLiabilityTypes = (assets) => {
    const isDev = $rootScope.isDev();
    const availableLiabilityTypes = Liability.getAvailableLiabilityTypes();
    const liabilityTypes = getAvailableLiabilityTypes();
    for (let i = 0; i < liabilityTypes.length; i += 1) {
      if (
        !helperService.hasAssetWithAssetTypeOwnerOccupiedOrInvestment({
          assets
        }) &&
        liabilityTypes[i].id === availableLiabilityTypes[0].id &&
        !isDev
      ) {
        liabilityTypes[i].isDisabled = true;
      } else if (
        !helperService.hasAssetWithAssetTypeOwnerOccupiedOrInvestment({
          assets,
          type: 'INV'
        }) &&
        helperService.getOrganizationCountry().cca2 === 'AU' &&
        liabilityTypes[i].id === availableLiabilityTypes[5].id &&
        !isDev
      ) {
        liabilityTypes[i].isDisabled = true;
      } else if (
        !helperService.hasAssetWithAssetTypeVehicle(assets) &&
        liabilityTypes[i].id === availableLiabilityTypes[3].id &&
        !isDev
      ) {
        liabilityTypes[i].isDisabled = true;
      } else {
        liabilityTypes[i].isDisabled = false;
      }
    }
    return liabilityTypes;
  };

  /**
   * Get ownership percentage per contact.
   *
   * @param {Boolean} isFirst
   * @param {Array} contacts
   *
   * @returns {Object}
   */
  const getOwnershipPercentagePerContact = (ownership, idContact) => {
    const oP = ownership.find((o) => o.idContact === idContact);
    return oP !== undefined ? helperService.isNumber(oP.percent, false) : 0;
  };

  /**
   * Add liability to liabilities array.
   *
   * @param {Array} liabilities
   * @param {Object} liabilityType
   * @param {Array} contacts
   *
   * @returns {Number}
   */
  const liabilityAdd = (liabilities, liabilityType, contacts) => {
    const ownership = helperService.setOwnershipOfAssetOrLiability(contacts, true);
    liabilities.push(Liability.blank(liabilityType, ownership));
    return liabilities;
  };

  /**
   * Remove liability from liabilities array.
   *
   * @param {Array} liabilities
   * @param {Number} index
   *
   * @returns {Array}
   */
  const liabilityDelete = (liabilities, index) =>
    helperService.removeItemFromArray(liabilities, index);

  /**
   * Get total liabilities balance per Contact.
   *
   * @param {Array} liabilities
   * @param {String} idContact
   *
   * @returns {Number}
   */
  const getTotalLiabilitiesBalancePerContact = (liabilities, idContact) => {
    let total = 0;
    for (let l = 0; l < liabilities.length; l += 1) {
      const ownership = liabilities[l].ownership.find((item) => item.idContact === idContact);
      total +=
        ownership && ownership.percent && helperService.isNumber(ownership.percent, true) !== 0
          ? (helperService.isNumber(ownership.percent, true) / 100) *
            helperService.isNumber(liabilities[l].balance, true)
          : 0;
    }
    return total;
  };

  /**
   * Get total liabilities balance.
   *
   * @param {Array} liabilities
   *
   * @returns {Number}
   */
  const getTotalLiabilitiesBalance = (liabilities, assetCommitmentSchedules) => {
    let total = 0;
    for (let l = 0; l < liabilities.length; l += 1) {
      total += helperService.isNumber(liabilities[l].balance, true);
    }
    if (assetCommitmentSchedules && assetCommitmentSchedules.length) {
      total += helperService.getAssetCommitmentScheduleTotal({
        assetCommitmentSchedules,
        field: 'outstanding'
      });
    }
    return total;
  };

  /**
   * Get total liabilities repayment per Contact.
   *
   * @param {Array} liabilities
   * @param {Object} workflow
   * @param {String} idContact
   *
   * @returns {Number}
   */
  const getTotalExistingLiabilitiesRepaymentPerContact = ({
    liabilities,
    workflow,
    idContact
  }) => {
    let total = 0;
    for (let l = 0; l < liabilities.length; l += 1) {
      const ownership = liabilities[l].ownership.find((item) => item.idContact === idContact);
      let repayment = 0;
      if (helperService.isLiabilityTypeCreditCard(liabilities[l].liabilityType)) {
        repayment =
          helperService.isNumber(liabilities[l].limit, true) *
          helperService.getAvailableRepaymentIndexes({ workflow }).creditCardRate;
      } else if (helperService.isLiabilityTypeOverdraft(liabilities[l].liabilityType)) {
        repayment =
          helperService.isNumber(liabilities[l].limit, true) *
          helperService.getAvailableRepaymentIndexes({ workflow }).overdraftRate;
      } else {
        repayment = helperService.isNumber(liabilities[l].repayment, true);
      }
      total +=
        helperService.isNumber(ownership.percent, true) !== 0
          ? (helperService.isNumber(ownership.percent, true) / 100) * repayment
          : 0;
    }
    return total;
  };

  /**
   * Get total existing liabilities repayment for proposed position at buffer rate.
   *
   * @param {Object} workflow
   * @param {Object} item
   * @param {Number} hasBuffer
   *
   * @returns {Number}
   */
  const getTotalProposedLiabilitiesRepayments = ({ workflow, item, hasBuffer }) => {
    let out = 0;
    if (workflow?.isHomeLoan()) {
      out = hasBuffer
        ? item?.repayments?.ongoingMonthlyRepaymentBuffer
        : item?.repayments?.ongoingMonthlyRepayment;
    }
    if (workflow?.isAssetFinance()) {
      out = hasBuffer ? item?.estimatedRepaymentBuffer || 0 : item?.estimatedRepayment || 0;
    }
    return out;
  };

  /**
   * Get liability value per ownership percentage.
   *
   * @param {Number} value
   * @param {Number} percent
   *
   * @returns {Number}
   */
  const getLiabilityBalancePerOwnershipPercentage = (value, percent) =>
    value * (helperService.isNumber(percent, false) / 100);

  /**
   * Get investment and not refinanced mortgage liabilities.
   *
   * @param {Array} liabilities
   *
   * @returns {Number}
   */
  const getInvestmentAndNotrefinancedMortgageLiabilities = (liabilities) =>
    _.filter(
      liabilities,
      (l) =>
        helperService.isLiabilityTypeMortgage(l.liabilityType) &&
        l.mortgageType &&
        !l.isRefinanced
    );

  return {
    getAllLiabilityTypes,
    getAvailableLiabilityTypes,
    getLiabilityTypes,
    getOwnershipPercentagePerContact,
    liabilityAdd,
    liabilityDelete,
    getTotalLiabilitiesBalancePerContact,
    getTotalLiabilitiesBalance,
    getTotalExistingLiabilitiesRepaymentPerContact,
    getTotalProposedLiabilitiesRepayments,
    getLiabilityBalancePerOwnershipPercentage,
    getInvestmentAndNotrefinancedMortgageLiabilities
  };
};
